"use client";
import React, { useState, useMemo } from "react";
import translate from "translate";
import { LanguageData } from "./LanguageData";
import { SearchAutocomplete } from "@/components/common/sidebar/SearchAutocomplete";
import { FiCopy, handleCopy } from "@/components/common/commonFunction/handleCopy";
import Loader from "@/components/Loader/Loader";
import commonStyles from "@/styles/component-style/common.module.scss";
import styles from "./LanguageFormat.module.scss";

type LanguageItem = { name: string; code: string };

interface LanguageSearchProps {
  languageData: LanguageItem[];
  onSelect: (item: { name: string; languageCode: string }) => void;
  formatResult: (item: { name: string; languageCode: string }) => React.ReactNode;
}

const LanguageSearch = ({
  languageData,
  onSelect,
  formatResult,
}: LanguageSearchProps) => (
  <SearchAutocomplete<{ name: string; languageCode: string }>
    placeholder="Search language. Ex - Russia(Russian)"
    items={
      languageData?.map((item) => ({
        name: item?.name ?? "",
        languageCode: item?.code ?? "",
      })) ?? []
    }
    onSelect={onSelect}
    autoFocus
    formatResult={formatResult}
    maxResults={20}
  />
);

function LanguageFormat() {
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState("");
  const [copy, setCopy] = useState(false);
  const [select, setSelect] = useState("");
  const [errors, setErrors] = useState("");
  const [errorsBox, setErrorsBox] = useState("");
  const [loading, setLoading] = useState(false);
  const [translateError, setTranslateError] = useState("");

  translate.engine = "google";
  translate.key =
    (typeof process !== "undefined" && process.env.NEXT_PUBLIC_GOOGLE_TRANSLATE_KEY) ||
    (typeof process !== "undefined" && process.env.GOOGLE_KEY) ||
    undefined;

  const selectedLanguageName = useMemo(
    () => LanguageData.find((item) => item.code === select)?.name ?? "",
    [select]
  );

  const handleOnErase = () => {
    setText("");
    setCopy(false);
    setAnswer("");
    setTranslateError("");
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setCopy(false);
    setAnswer("");
    setErrors("");
    setTranslateError("");
  };

  const formatResult = (item: { name: string; languageCode: string }) => (
    <span className={commonStyles.autocompleteResultItem}>{item.name}</span>
  );

  const handleOnSelect = (item: { name: string; languageCode: string }) => {
    setErrorsBox("");
    setCopy(false);
    setSelect(item?.languageCode ?? "");
    setTranslateError("");
  };

  const handleOnSubmit = async () => {
    setTranslateError("");
    if (!isValidate()) return;
    setLoading(true);
    try {
      const textConverted = await translate(text, select);
      setAnswer(typeof textConverted === "string" ? textConverted : String(textConverted ?? ""));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Translation failed. Please try again.";
      setTranslateError(message);
      setAnswer("");
    } finally {
      setLoading(false);
    }
  };

  const isValidate = () => {
    let formIsValid = true;
    if (!text || text.trim() === "") {
      setErrors("* Please enter text");
      formIsValid = false;
    } else {
      setErrors("");
    }
    if (!select || select.trim() === "") {
      setErrorsBox("* Please select language");
      formIsValid = false;
    } else {
      setErrorsBox("");
    }
    return formIsValid;
  };

  return (
    <>
      <div className="main-globally-counter-box">
        {text?.length > 0 && (
          <div className="color-picker-icon">
            <i
              className="fa-solid fa-eraser"
              title="Clear"
              onClick={handleOnErase}
              role="button"
              aria-label="Clear text"
            />
          </div>
        )}
        <div className="counter-text-area">
          <textarea
            className={errors ? "error-input" : ""}
            placeholder="Type or paste text here to translate"
            name="firstInput"
            value={text}
            onChange={handleOnChange}
          />
          <span className={styles.errorText}>{errors}</span>
        </div>
      </div>

      <div className="language-search-box react-search-auto-fill coverter-tools">
        <div className={errorsBox ? "language-error-text" : ""}>
          <LanguageSearch
            languageData={LanguageData}
            onSelect={handleOnSelect}
            formatResult={formatResult}
          />
        </div>
        {selectedLanguageName && (
          <p className={`language-selected-label ${styles.selectedLabel}`}>
            Translate to: <strong>{selectedLanguageName}</strong>
          </p>
        )}
        <span className={styles.errorText}>{errorsBox}</span>
        {translateError && (
          <p className={styles.translateError}>{translateError}</p>
        )}
        <div className="button lowercase-center-alignment">
          <button type="button" onClick={handleOnSubmit} disabled={loading}>
            {loading ? <Loader /> : "Translate"}
          </button>
        </div>
      </div>
      {answer && (
        <div className="main-globally-counter-box">
          <div className="copy-textarea-icon-alignment">
            <FiCopy onClick={() => handleCopy(answer, setCopy)} />
            {copy && <span>Copied.</span>}
          </div>
          <div className="counter-text-area">
            <textarea
              disabled
              readOnly
              placeholder="Translation will appear here"
              name="firstInput"
              value={answer}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default LanguageFormat;
